import { useEffect, useState } from "react";
import adminApi, { fmtDate, downloadAdminFile } from "../../lib/adminApi";
import { formatErr } from "../../lib/api";
import { MagnifyingGlass, Check, X, FileArrowDown } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";

const TABS = [
  { key: "PENDING", label: "В ожидании" },
  { key: "APPROVED", label: "Одобрены" },
  { key: "REJECTED", label: "Отклонены" },
  { key: "all", label: "Все" },
];
const STATUS_STYLE = {
  PENDING: "bg-[#E3B341]/15 text-[#E3B341]",
  APPROVED: "bg-[#0ECB81]/15 text-[#0ECB81]",
  REJECTED: "bg-[#F6465D]/15 text-[#F6465D]",
};

export default function AdminProp() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("PENDING");
  const [q, setQ] = useState("");
  const [rejecting, setRejecting] = useState(null);

  const load = () => adminApi.get("/admin/prop", { params: { status, q } })
    .then((r) => setItems(r.data)).catch((e) => toast.error(formatErr(e)));

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);

  const decide = async (item, approve, note = "") => {
    try {
      await adminApi.post(`/admin/prop/${item.id}/decide`, { approve, note });
      toast.success(approve ? "Заявка одобрена" : "Заявка отклонена");
      setRejecting(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const dl = async (item, f) => {
    try { await downloadAdminFile(`/admin/prop/${item.id}/file/${f.id}`, f.original_filename); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div data-testid="admin-prop">
      <h2 className="text-lg font-semibold text-white mb-4">Prop-заявки</h2>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex gap-1 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setStatus(t.key)}
              data-testid={`prop-tab-${t.key}`}
              className={`px-3 h-10 rounded-lg text-xs font-medium transition-colors ${status === t.key ? "bg-[#E3B341]/15 text-[#E3B341] ring-1 ring-inset ring-[#E3B341]/30" : "bg-[#111827] text-[#6B7280] hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по имени, фирме, account ID…"
            className="h-10 pl-9 bg-[#111827] border-[#1F2937] text-white rounded-lg" data-testid="prop-search" />
        </div>
      </div>

      <div className="space-y-2">
        {items.map((p) => (
          <div key={p.id} className="bg-[#111827] border border-[#1F2937] rounded-xl p-4" data-testid={`prop-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{p.first_name} {p.last_name}</span>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full ${STATUS_STYLE[p.status] || "bg-white/5 text-[#6B7280]"}`}>{p.status}</span>
                </div>
                <p className="text-xs text-[#9CA3AF] mt-1">Фирма: <span className="text-white">{p.prop_firm}</span> · Account ID: <span className="mono">{p.account_id}</span></p>
                <p className="text-[11px] text-[#6B7280] mt-0.5">{p.user_email || p.user_id}</p>
                {p.comment && <p className="text-[11px] text-[#9CA3AF] mt-1 italic">«{p.comment}»</p>}
                <p className="text-[10px] text-[#4B5563] mt-1">{fmtDate(p.created_at)}</p>
                {p.files?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {p.files.map((f) => (
                      <button key={f.id} onClick={() => dl(p, f)}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-[#0A0E17] border border-[#1F2937] text-[#9CA3AF] hover:text-white hover:border-[#E3B341]/40"
                        data-testid={`prop-file-${f.id}`}>
                        <FileArrowDown size={13} /> {f.original_filename}
                      </button>
                    ))}
                  </div>
                )}
                {p.admin_note && <p className="text-[11px] text-[#9CA3AF] mt-1">Заметка: {p.admin_note}</p>}
              </div>
              {p.status === "PENDING" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => decide(p, true)}
                    className="h-9 bg-[#0ECB81] hover:bg-[#0bb673] text-[#0A0E17] font-semibold" data-testid={`prop-approve-${p.id}`}>
                    <Check size={16} className="mr-1" /> Одобрить
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRejecting(p)}
                    className="h-9 text-[#F6465D] hover:bg-[#F6465D]/10" data-testid={`prop-reject-${p.id}`}>
                    <X size={16} className="mr-1" /> Отклонить
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-[#6B7280] py-8">Нет заявок</div>}
      </div>

      <RejectDialog item={rejecting} onClose={() => setRejecting(null)} onConfirm={(note) => decide(rejecting, false, note)} />
    </div>
  );
}

function RejectDialog({ item, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [item]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-white" data-testid="prop-reject-dialog">
        <DialogHeader><DialogTitle>Отклонить prop-заявку</DialogTitle></DialogHeader>
        <DialogDescription className="text-[#9CA3AF] text-sm">Заявка будет отклонена. Можно указать причину.</DialogDescription>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Причина отклонения (опц.)"
          className="bg-[#0A0E17] border-[#1F2937] text-white" data-testid="prop-reject-note" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#9CA3AF]">Отмена</Button>
          <Button onClick={() => onConfirm(note)} className="bg-[#F6465D] hover:bg-[#d93a4f] text-white" data-testid="prop-reject-confirm">
            Отклонить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
